/**
 * Administration — screens 07 (users), 08 (report access) and 10 (companies).
 *
 * These three screens are the only place the answer to "who sees what" is written down,
 * so the rules they enforce live here, beside the two predicates that read them
 * (`contractorScope` in auth.ts, `resolveReportAccess` in grants.ts) rather than inside a
 * dialog's submit handler.
 *
 * ⚠ **The rule that matters most is `roleFitsCompany`.** `contractorScope` returns `null`
 * — meaning *no filter, every row* — for `SYSTEM_ADMIN` and `UNIFIBER_INTERNAL`, and it
 * does so WITHOUT LOOKING AT THE COMPANY. So a person given the Unifiber Internal role
 * while sitting in Circet's company does not see Circet's rows: they see everybody's,
 * including Constructel's. Two dropdowns side by side in a dialog, one wrong click, and
 * the product's central promise is gone with no error anywhere. The API refuses the
 * combination and the screen explains why rather than just greying it out.
 */
import { isInternal } from "./auth.js";
export const COMPANY_KINDS = ["INTERNAL", "CONTRACTOR"];
// ─────────────────────────────────────────────────────────────────────────────
// The role/company rule
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Is this role coherent inside a company of this kind?
 *
 * Both directions are refused, for different reasons:
 *
 *   · An INTERNAL role in a CONTRACTOR company is a **leak**. It is unscoped, so that
 *     person sees every contractor's backlog from inside one of them.
 *   · An EXTERNAL_OPERATOR in the INTERNAL company is **useless**: Unifiber holds no Monday
 *     contractor label, so `matchesScope` matches nothing and they see an empty report
 *     that looks broken rather than empty-by-design.
 */
export function roleFitsCompany(role, kind) {
    return isInternal(role) ? kind === "INTERNAL" : kind === "CONTRACTOR";
}
/** Why the pairing was refused, in the words the screen shows. */
export function roleCompanyProblem(role, kind) {
    if (roleFitsCompany(role, kind))
        return null;
    return isInternal(role)
        ? "An internal role is never row-scoped, so inside a contractor's company it would see every contractor's rows, not that company's. Use External Operator, or move them to Unifiber."
        : "External Operator scopes a person to their company's Monday contractor labels, and Unifiber has none — they would open the report and find it empty. Use Unifiber Internal or Administrator.";
}
/**
 * ⚠ The account has a password path and no second factor yet.
 *
 * Not "has never signed in": somebody can sign in, be forced through the password change,
 * and stop before scanning the QR code. Until the authenticator exists they cannot reach a
 * report, so the screen counts them as still to finish rather than as a working account.
 */
export function isPendingEnrolment(user) {
    return user.authType !== "ENTRA_ID" && !user.totpEnabled;
}
export function filterUsers(users, filters) {
    const needle = filters.search?.trim().toLowerCase();
    return users.filter((user) => {
        if (filters.companyId && user.companyId !== filters.companyId)
            return false;
        if (filters.status === "active" && !user.isActive)
            return false;
        if (filters.status === "disabled" && user.isActive)
            return false;
        if (filters.auth === "entra" && user.authType === "LOCAL")
            return false;
        // ⚠ BOTH counts as a password account here. It is one, and the question this filter
        // answers is "who could sign in with a password", which is a security question.
        if (filters.auth === "local" && user.authType === "ENTRA_ID")
            return false;
        if (needle) {
            const haystack = `${user.displayName} ${user.email}`.toLowerCase();
            if (!haystack.includes(needle))
                return false;
        }
        return true;
    });
}
// ─────────────────────────────────────────────────────────────────────────────
// Companies — screen 10
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The colours a company may be drawn in.
 *
 * The first three are the brand gradient's own stops, so the common case looks like
 * Unifiber rather than like a chart library. Two companies may not share one: the whole
 * point of a per-company colour is telling them apart on a stacked bar.
 */
export const COMPANY_PALETTE = [
    "#84257a",
    "#ed125f",
    "#f47b3b",
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#0ea5e9",
];
/** Lower-case, no spaces — it appears in exports and links. */
export function isValidCompanyCode(code) {
    return /^[a-z0-9][a-z0-9-]{1,30}$/.test(code);
}
export function companyImpact(company) {
    return {
        users: company.activeUsers,
        reportGrants: company.reportGrants,
        // ⚠ Counts EVERY user, not just active ones: a disabled account still points at this
        // company, and the foreign key is `onDelete: Restrict`. Offering a delete that the
        // database will refuse is worse than not offering it.
        removable: company.users === 0,
    };
}
/**
 * The labels as an administrator typed them, ready to store: trimmed, blanks dropped,
 * duplicates removed — and NOTHING else.
 *
 * ⚠ Never lower-cased and never re-spaced. `CIF - Circet` is not `cif - circet` and not
 * `CIF-Circet`: the scope matches the board's spelling exactly, so "normalising" a label
 * here would quietly make a contractor match no rows at all.
 */
export function normaliseMondayLabels(labels) {
    return [...new Set(labels.map((label) => label.trim()).filter(Boolean))];
}
//# sourceMappingURL=admin.js.map